import React, { useEffect, useState } from "react";
import { Trash2, Undo2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, EmptyState, Loader } from "@/components/dashboard/ui";
import { getTrash, trashAction, emptyTrash } from "@/lib/trash";
import { apiError } from "@/lib/api";

export default function AdminTrash() {
  const { hasRole } = useAuth();
  const [groups, setGroups] = useState(null);
  const [sel, setSel] = useState({});
  const isSuper = hasRole("super_admin");

  const load = () => getTrash().then((d) => { setGroups(d || []); setSel({}); }).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const key = (e, id) => `${e}:${id}`;
  const toggle = (e, id) => setSel((s) => ({ ...s, [key(e, id)]: !s[key(e, id)] }));
  const groupSelected = (g) => g.items.filter((it) => sel[key(g.entity, it.id)]).map((it) => it.id);

  const run = async (entity, ids, action) => {
    if (!ids.length) return;
    if (action === "purge" && !window.confirm(`Supprimer définitivement ${ids.length} élément(s) ? Cette action est irréversible.`)) return;
    try {
      const { data } = await trashAction(entity, ids, action);
      toast.success(action === "restore" ? `${data.affected} élément(s) restauré(s)` : `${data.affected} supprimé(s) définitivement`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doEmpty = async () => {
    if (!window.confirm("Vider toute la corbeille ? Les éléments sans dépendances seront supprimés définitivement.")) return;
    try { const { data } = await emptyTrash(); toast.success(`${data.affected} élément(s) supprimé(s)`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (groups === null) return <Loader />;
  const totalCount = groups.reduce((n, g) => n + g.count, 0);

  return (
    <div>
      <PageHeader title="Corbeille" subtitle="Éléments supprimés. Restaurez-les ou supprimez-les définitivement."
        action={isSuper && totalCount > 0 && <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={doEmpty} data-testid="empty-trash-btn"><Trash2 className="h-4 w-4 mr-1.5" />Vider la corbeille</Button>} />
      {totalCount === 0 ? (
        <EmptyState icon={Trash2} title="La corbeille est vide" text="Les éléments supprimés depuis les autres rubriques apparaîtront ici." />
      ) : (
        <div className="space-y-5">
          {groups.filter((g) => g.count > 0).map((g) => {
            const gs = groupSelected(g);
            return (
              <div key={g.entity} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`trash-group-${g.entity}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-semibold text-slate-900">{g.label} <span className="text-slate-400 font-normal">({g.count})</span></h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={!gs.length} onClick={() => run(g.entity, gs, "restore")} data-testid={`restore-sel-${g.entity}`}><Undo2 className="h-4 w-4 mr-1" />Restaurer</Button>
                    {isSuper && <Button size="sm" variant="ghost" className="text-rose-600" disabled={!gs.length} onClick={() => run(g.entity, gs, "purge")} data-testid={`purge-sel-${g.entity}`}><Trash2 className="h-4 w-4 mr-1" />Supprimer déf.</Button>}
                  </div>
                </div>
                <div>
                  {g.items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-0" data-testid={`trash-item-${it.id}`}>
                      <Checkbox checked={!!sel[key(g.entity, it.id)]} onCheckedChange={() => toggle(g.entity, it.id)} data-testid={`trash-check-${it.id}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.label}</p>
                        {it.sub && <p className="text-xs text-slate-400 truncate">{it.sub}</p>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0 hidden sm:block">{it.deleted_at ? new Date(it.deleted_at).toLocaleDateString("fr-CH") : ""}</span>
                      <Button size="sm" variant="ghost" title="Restaurer" onClick={() => run(g.entity, [it.id], "restore")} data-testid={`restore-${it.id}`}><Undo2 className="h-4 w-4" /></Button>
                      {isSuper && <Button size="sm" variant="ghost" className="text-rose-500" title="Supprimer définitivement" onClick={() => run(g.entity, [it.id], "purge")} data-testid={`purge-${it.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {!isSuper && <p className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" />La suppression définitive est réservée au super administrateur.</p>}
        </div>
      )}
    </div>
  );
}
